import React, { createContext, useContext, useState, useEffect } from 'react';
import firebaseService, { DEMO_MODE } from '../services/FirebaseService';

// Create context
const AuthContext = createContext();

// Provider component
export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null); // Includes tier, isAdmin, etc.
  const [loading, setLoading] = useState(true);

  // Setup demo user for demo mode
  useEffect(() => {
    setLoading(true);

    // In demo mode, use a fake user with premium tier
    if (DEMO_MODE) {
      setCurrentUser({
        uid: 'demo-user-123',
        email: 'demo@example.com',
        displayName: 'Demo User'
      });
      setUserProfile({
        tier: 'premium',
        isAdmin: true,
        matchPullsToday: 0,
        lastMatchPullDate: null
      });
      setLoading(false);
      return;
    }

    // Otherwise use the real auth state
    const unsubscribe = firebaseService.onAuthStateChanged(async (user) => {
      setCurrentUser(user);

      // Load user profile with tier data
      if (user) {
        try {
          const profile = await firebaseService.getUserProfile(user.uid);
          setUserProfile(profile);

          // Update lastActive timestamp on sign in
          await firebaseService.updateLastActive(user.uid);
        } catch (error) {
          console.error('Error loading user profile:', error);
          // Set default free tier on error
          setUserProfile({
            tier: 'free',
            isAdmin: false,
            matchPullsToday: 0,
            lastMatchPullDate: null
          });
        }
      } else {
        setUserProfile(null);
      }

      setLoading(false);
    });

    return unsubscribe;
  }, []);

  // Sign in
  async function signIn(email, password) {
    if (DEMO_MODE) {
      // In demo mode, always succeed with a fake user
      setCurrentUser({
        uid: 'demo-user-123',
        email: email || 'demo@example.com',
        displayName: 'Demo User'
      });
      return { user: currentUser };
    }
    return firebaseService.signIn(email, password);
  }

  // Sign up
  async function signUp(email, password, displayName) {
    if (DEMO_MODE) {
      // In demo mode, always succeed with a fake user
      setCurrentUser({
        uid: 'demo-user-123',
        email: email || 'demo@example.com',
        displayName: displayName || 'Demo User'
      });
      return { user: currentUser };
    }
    return firebaseService.signUp(email, password, displayName);
  }

  // Sign out
  async function signOut() {
    if (DEMO_MODE) {
      // In demo mode, don't actually sign out
      window.location.href = '/';
      return Promise.resolve();
    }
    await firebaseService.signOut();
    // No need to redirect here as it's handled in FirebaseService
  }

  // Google sign-in
  async function signInWithGoogle() {
    if (DEMO_MODE) {
      // In demo mode, always succeed with a fake user
      setCurrentUser({
        uid: 'demo-user-123',
        email: 'demo@example.com',
        displayName: 'Demo User'
      });
      return { user: currentUser };
    }
    return firebaseService.signInWithGoogle();
  }

  const value = {
    currentUser,
    userProfile, // Includes tier, isAdmin, matchPullsToday, etc.
    loading,
    signIn,
    signUp,
    signOut,
    signInWithGoogle
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

// Custom hook to use auth
export function useAuth() {
  return useContext(AuthContext);
} 